import { Controller } from "@hotwired/stimulus"
import Hammer from "hammerjs"

export default class extends Controller {
  static targets = ["feed", "reel", "video", "progress", "pauseIcon", "spinner", "muteBtn", "mutedIcon", "unmutedIcon", "tapArea", "topBar"]
  static values  = { showRaw: Boolean }

  #bufferListeners = new WeakMap()

  connect() {
    // Lock body scroll so only the feed scrolls (eliminates double scrollbar)
    document.documentElement.style.overflow = "hidden"
    document.body.style.overflow = "hidden"

    this.observer = new IntersectionObserver(this.#handleIntersection.bind(this), {
      root: this.feedTarget,
      threshold: 0.7,
    })
    this.reelTargets.forEach(reel => this.observer.observe(reel))
    this.#setupHammer()
    this.#setupNavSync()
  }

  disconnect() {
    document.documentElement.style.overflow = ""
    document.body.style.overflow = ""

    document.removeEventListener("gallery:nav-visible", this._onNavVisible)
    document.removeEventListener("gallery:nav-hidden",  this._onNavHidden)

    this.observer?.disconnect()
    this.#clearProgressTimer()
    this.videoTargets.forEach(v => this.#unbindBufferEvents(v))
    this._hammerFeed?.destroy()
    this._reelHammers?.forEach(h => h.destroy())
  }

  // IntersectionObserver: autoplay the reel that enters the viewport
  #handleIntersection(entries) {
    entries.forEach(entry => {
      const reel  = entry.target
      const video = reel.querySelector("[data-gallery-reel-target='video']")
      if (!video) return

      if (entry.isIntersecting) {
        video.play().catch(() => {})
        this.#startProgress(reel, video)
        this.#bindBufferEvents(reel, video)
        document.dispatchEvent(new CustomEvent("gallery:reel-changed"))
      } else {
        video.pause()
        video.currentTime = 0
        this.#resetProgress(reel)
        this.#unbindBufferEvents(video)
        this.#setSpinner(reel, false)
      }
    })
  }

  togglePlay({ currentTarget }) {
    const reel  = currentTarget.closest("[data-gallery-reel-target='reel']")
    const video = reel?.querySelector("[data-gallery-reel-target='video']")
    const icon  = reel?.querySelector("[data-gallery-reel-target='pauseIcon']")
    if (!video) return

    if (video.paused) {
      video.play()
      this.#fadePauseIcon(icon, false)
    } else {
      video.pause()
      this.#fadePauseIcon(icon, true)
    }
  }

  toggleMute() {
    const activeReel = this.reelTargets.find(r => r.dataset.active === "true")
    const video = activeReel?.querySelector("[data-gallery-reel-target='video']")
    if (!video) return

    video.muted = !video.muted
    this.#updateMuteIcons(video.muted)
  }

  // ── Nav sync — top bar follows navbar visibility ──────────────────────────────

  #setupNavSync() {
    if (!this.hasTopBarTarget) return

    this._onNavVisible = () => {
      this.topBarTarget.classList.remove("gallery-top-bar--nav-hidden")
      this.topBarTarget.classList.add("gallery-top-bar--nav-visible")
    }
    this._onNavHidden = () => {
      this.topBarTarget.classList.remove("gallery-top-bar--nav-visible")
      this.topBarTarget.classList.add("gallery-top-bar--nav-hidden")
    }

    document.addEventListener("gallery:nav-visible", this._onNavVisible)
    document.addEventListener("gallery:nav-hidden",  this._onNavHidden)
  }

  // ── Hammer setup ─────────────────────────────────────────────────────────────

  #setupHammer() {
    // Swipe up/down on the feed → navigate reels
    this._hammerFeed = new Hammer.Manager(this.feedTarget, {
      recognizers: [
        [Hammer.Swipe, { direction: Hammer.DIRECTION_VERTICAL, threshold: 10, velocity: 0.3 }],
      ],
      touchAction: "pan-x",
    })

    this._hammerFeed.on("swipeup",   () => this.#scrollToReel(1))
    this._hammerFeed.on("swipedown", () => this.#scrollToReel(-1))

    // Per-reel: double-tap heart + swipe left/right seek
    this._reelHammers = this.reelTargets.map(reel => {
      const mc = new Hammer.Manager(reel, { touchAction: "pan-y" })

      // DoubleTap must be added before Tap so requireFailure works correctly
      const swipe     = new Hammer.Swipe({ direction: Hammer.DIRECTION_HORIZONTAL, threshold: 20, velocity: 0.4 })
      const doubleTap = new Hammer.Tap({ event: "doubletap", taps: 2 })
      const singleTap = new Hammer.Tap()

      mc.add([swipe, doubleTap, singleTap])
      singleTap.requireFailure(doubleTap)

      mc.on("doubletap", (e) => this.#showHeart(reel, e.center.x, e.center.y))
      mc.on("swipeleft",  () => this.#seekActiveVideo(10))
      mc.on("swiperight", () => this.#seekActiveVideo(-10))

      // Single tap → play/pause. Skip real controls (mute, links) but allow tapArea.
      // requireFailure(doubleTap) ensures this never fires during a double-tap.
      mc.on("tap", (e) => {
        if (e.target.closest("a, button:not([data-gallery-reel-target='tapArea'])")) return
        this.#togglePlayForReel(reel)
      })

      return mc
    })
  }

  #togglePlayForReel(reel) {
    const video = reel.querySelector("[data-gallery-reel-target='video']")
    const icon  = reel.querySelector("[data-gallery-reel-target='pauseIcon']")
    if (!video) return
    if (video.paused) {
      video.play()
      this.#fadePauseIcon(icon, false)
    } else {
      video.pause()
      this.#fadePauseIcon(icon, true)
    }
  }

  #scrollToReel(delta) {
    const activeIndex = this.reelTargets.findIndex(r => r.dataset.active === "true")
    const next = this.reelTargets[activeIndex + delta]
    if (!next) return
    next.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  #showHeart(reel, x, y) {
    const heart = document.createElement("div")
    heart.className = "gallery-heart"
    heart.textContent = "❤️"
    heart.style.left = `${x}px`
    heart.style.top  = `${y}px`
    reel.appendChild(heart)
    heart.addEventListener("animationend", () => heart.remove())
  }

  #seekActiveVideo(seconds) {
    const activeReel = this.reelTargets.find(r => r.dataset.active === "true")
    if (!activeReel) return

    const video = activeReel.querySelector("[data-gallery-reel-target='video']")
    if (!video) return

    video.currentTime = Math.max(0, Math.min(video.duration || 0, video.currentTime + seconds))
    this.#showSeekNudge(activeReel, seconds)
  }

  #showSeekNudge(reel, seconds) {
    const nudge = document.createElement("div")
    const isForward = seconds > 0
    nudge.className = `gallery-seek-nudge gallery-seek-nudge--${isForward ? "right" : "left"}`
    nudge.innerHTML = `
      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
        ${isForward
          ? '<path d="M18 13a6 6 0 11-6-6v3l4-4-4-4v3a8 8 0 108 8h-2z"/><text x="12" y="15" text-anchor="middle" font-size="7" fill="currentColor">+</text>'
          : '<path d="M6 13a6 6 0 106-6v3l-4-4 4-4v3a8 8 0 10-8 8h2z"/>'}
      </svg>
      ${Math.abs(seconds)}s
    `
    reel.appendChild(nudge)
    nudge.addEventListener("animationend", () => nudge.remove())
  }

  // ── Private helpers ───────────────────────────────────────────────────────────

  #bindBufferEvents(reel, video) {
    this.#unbindBufferEvents(video)

    const show = () => this.#setSpinner(reel, true)
    const hide = () => this.#setSpinner(reel, false)

    video.addEventListener("waiting", show)
    video.addEventListener("playing", hide)
    video.addEventListener("canplay", hide)
    this.#bufferListeners.set(video, { show, hide })

    if (video.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) show()
  }

  #unbindBufferEvents(video) {
    const listeners = this.#bufferListeners.get(video)
    if (!listeners) return
    video.removeEventListener("waiting", listeners.show)
    video.removeEventListener("playing", listeners.hide)
    video.removeEventListener("canplay", listeners.hide)
    this.#bufferListeners.delete(video)
  }

  #setSpinner(reel, visible) {
    const spinner = reel.querySelector("[data-gallery-reel-target='spinner']")
    if (spinner) spinner.style.opacity = visible ? "1" : "0"
  }

  #fadePauseIcon(icon, show) {
    if (!icon) return
    icon.style.opacity = show ? "1" : "0"
    if (show) setTimeout(() => { icon.style.opacity = "0" }, 800)
  }

  #updateMuteIcons(muted) {
    this.mutedIconTargets.forEach(el   => el.classList.toggle("hidden", !muted))
    this.unmutedIconTargets.forEach(el => el.classList.toggle("hidden", muted))
  }

  #startProgress(reel, video) {
    reel.dataset.active = "true"
    this.#clearProgressTimer()

    const bar = reel.querySelector("[data-gallery-reel-target='progress']")
    if (!bar) return

    this.progressTimer = setInterval(() => {
      if (video.duration && video.duration > 0) {
        bar.style.width = `${(video.currentTime / video.duration) * 100}%`
      }
    }, 100)
  }

  #resetProgress(reel) {
    delete reel.dataset.active
    const bar = reel.querySelector("[data-gallery-reel-target='progress']")
    if (bar) bar.style.width = "0%"
  }

  #clearProgressTimer() {
    if (this.progressTimer) {
      clearInterval(this.progressTimer)
      this.progressTimer = null
    }
  }
}
